/* eslint-disable max-lines-per-function */
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';
import { setNameTextStyle } from '../../../helper/standardChart';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicRankingLine',
  name_en: 'Dynamic Ranking Line',
  name_zh: '动态排名折线图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 2,
    y: amount => amount > 1,
    z: amount => amount > 3,
    data: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER, DEFINE.STR],
      category: DEFINE.SINGLE
    },
    z: { itemType: [DEFINE.STR], category: DEFINE.SINGLE }
  }
};

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
  };
}

export const dynamicRankingLine = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  withTimeline: true,
  render(api, spec, $dom) {
    const { data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z }
    } = spec;
    const data = dataArray[dataIndex];
    const dataMetaInfo = hook.parseDataMetaInfo(data);

    // * ------------------------- data
    const seriesDataByName = {};

    const transformDate = util.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);
    const { xAxisData, seriesNames } = util.dataEncoding.encoding(transformDate, {
      xAxisData: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      seriesNames: { field: z, transforms: [{ type: 'deDuplication' }] }
    });

    // * ------------------------- chart
    seriesNames.forEach(seriesName => {
      const seriesData = util.dataTransform.transform(transformDate.slice(), [
        { type: 'filter', cb: item => item[z] === seriesName }
      ]);

      seriesDataByName[seriesName] = [];

      xAxisData.forEach(date => {
        const dataItem = util.dataTransform.transform(seriesData.slice(), [
          { type: 'find', cb: item => item[x] === date }
        ]);
        seriesDataByName[seriesName].push({ __origin: dataItem });
      });
    });

    const seriesNamesTemp = new Set();
    const maxRange = xAxisData.length - 1;

    // 对字段进行排序
    xAxisData.forEach((date, index) => {
      const columns = [];
      seriesNames.forEach(seriesName => {
        const dataItem = util.dataTransform.transform(seriesDataByName[seriesName].slice(), [
          { type: 'find', cb: item => item?.__origin?.[x] === date }
        ]);
        if (dataItem) {
          columns.push(dataItem);
        }
      });

      columns.sort((a, b) => Number(b.__origin[y]) - Number(a.__origin[y]));

      columns.forEach((item, idx) => {
        // 排名结果
        // item.value = [index, idx];
        item.value = String(idx + 1);

        // 收集第一列数据的排名信息
        seriesNamesTemp.add(item.__origin[z]);
      });
    });

    const newSeriesNames = Array.from(seriesNamesTemp);

    const getSeries = (name, seriesData, xData) => {
      return {
        clip: true,
        withTimeline: {
          range: [0, 5],
          curIndex: xData.length - 1,
          maxRange: maxRange > 5 ? 5 : maxRange
        },
        name,
        type: 'dvRankLine',
        data: seriesData,
        lineStyle: {
          width: 1
        },
        emphasis: {
          focus: 'series',
          itemStyle: {
            borderColor: '#3b3b3b',
            borderWidth: 2
          }
        },
        symbol: 'circle',
        labelLayout: {
          labelOverlap: 'shiftY'
        },
        animationDurationUpdate: 1000,
        animationDuration: 1000
      };
    };

    // 渲染图表
    const range = 5;
    const option = {
      grid: {
        dvBoxLayout: null,
        top: 14
      },
      tooltip: {
        formatter: params_ => {
          const series = Array.isArray(params_) ? params_ : [params_];
          const items = [];

          series.forEach(item => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP.line,
              color: item.color,
              name: item.seriesName,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: series[0]?.name,
            items
          });
        }
      },
      xAxis: [
        {
          show: true,
          type: 'category',
          position: 'top',
          name: x + (dataMetaInfo[x]?.unit ? `(${dataMetaInfo[x].unit})` : ''),
          data: xAxisData,
          boundaryGap: false,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false,
            interval: 0
          },
          axisLabel: {
            dvAlignEdge: true
          }
        }
      ],
      yAxis: [
        {
          show: true,
          type: 'category',
          // name: '排名',
          inverse: true,
          axisLabel: {
            margin: 15
          },
          boundaryGap: false,
          axisTick: {
            show: false,
            alignWithLabel: false
          },
          axisLine: {
            show: false
          },
          splitLine: {
            show: false,
            interval: 0,
            lineStyle: {
              opacity: 0.5
            }
          },
          data: Array.from(Array(newSeriesNames.length), (_, index) => String(index + 1))
        }
      ],
      dataZoom: [
        {
          type: 'slider',
          show: false,
          orient: 'vertical'
        },
        {
          type: 'slider',
          show: false,
          startValue: xAxisData.length - 1 - range,
          endValue: xAxisData.length - 1,
          minSpanValue: range,
          maxSpanValue: range
        }
      ],
      series: newSeriesNames.map(name => {
        return getSeries(name, seriesDataByName[name], xAxisData);
      }),
      animationDurationUpdate: 1000,
      animationEasing: 'linear',
      animationEasingUpdate: 'linear'
    };

    return renderApi => {
      const timelineIns = renderApi.renderTimelineProxy(xAxisData);
      setNameTextStyle($dom, option);
      let renderParams = {
        option: renderApi.mergeChartOptionProxy(option)
      };
      const legendIns = renderApi.renderLegendProxy(renderParams.option.legend.data);
      const chartIns = renderApi.renderChartProxy(renderParams);

      const handleTimeLineChange = ({ index, causeBy }) => {
        let opt = {
          animation: causeBy === 'play'
        };
        if (index <= range) {
          opt = {
            dataZoom: [
              {},
              {
                startValue: 0,
                endValue: range
              }
            ],
            series: option.series.slice().map(() => {
              return {
                clip: 'strict',
                withTimeline: {
                  range: [0, index],
                  curIndex: index
                }
              };
            })
          };
        } else {
          opt = {
            dataZoom: [
              {},
              {
                startValue: index - range,
                endValue: index
              }
            ],
            series: option.series.slice().map(() => {
              return {
                clip: 'strict',
                withTimeline: {
                  range: [0, range],
                  curIndex: index
                }
              };
            })
          };
        }

        const { merge } = ThsDataVStandardChart.echarts.zrUtil;
        const newOpt = renderApi.mergeChartOptionProxy(merge(option, opt));
        legendIns?.update(newOpt.legend.data);

        renderParams = {
          option: opt,
          opts: {
            notMerge: false
          }
        };
        api.hook.beforeRenderStandardChart(api, renderParams);
        chartIns.play(renderParams);

        renderApi.cacheRenderOptionProxy(renderParams.option);
      };

      if (timelineIns instanceof api.ui.Dropdown) {
        timelineIns?.on?.('change', e => {
          handleTimeLineChange({ index: e.index, causeBy: 'click' });
        });
      } else {
        timelineIns?.on?.('change', handleTimeLineChange);
      }
    };
  }
});
